import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

import { getSelectedBranchId } from '../utils/branch';
import HeatmapComponent from './HeatmapComponent';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];
// Paleta fija por posición, no por año: así el color de un año no cambia cuando se prende otro.
const COLORES_ANIO = ['#007bff', '#17c671', '#c4183c', '#ffb400', '#5a6169', '#00b8d8'];

// Etiquetas del eje de plata. Sin esto, un ingreso de 1.953.773 se dibuja como "1953773" y no
// entra en el ancho por defecto del eje (60 px): recharts lo RECORTA por la izquierda y queda
// "953773" — se lee como si faltara una cifra, porque falta.
const ejeMoneda = (v) => {
    const n = Math.abs(Number(v));
    if (n >= 1000000) return `$${(Number(v) / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${Math.round(Number(v) / 1000)}k`;
    return `$${Math.round(Number(v))}`;
};

var config = process.env;

// ── Reglas de presentación de esta pantalla ──────────────────────────────────
//
// 1. UN DATO QUE NO SE PUEDE CALCULAR SE MUESTRA "—", NUNCA 0.
//    En varias pantallas del sistema un error se renderiza como 0 y se lee como información
//    ("no vendimos nada"). Acá el backend distingue: devuelve `null` cuando no sabe y `0`
//    cuando de verdad es cero. Esa distinción no se puede perder en el render.
//
// 2. EL MARGEN VIENE CON SU COBERTURA. El backend calcula el margen sobre las líneas cuyo costo
//    conoce y dice qué porción del ingreso es esa. Si no es el 100%, la tarjeta lo aclara: un
//    margen "60%" calculado sobre el 40% del ingreso no es el margen del negocio.
//
// 3. LAS FECHAS VAN COMO 'YYYY-MM-DD' Y SE ARMAN A MANO. `toISOString()` convierte a UTC y entre
//    las 21:00 y las 24:00 de Uruguay devuelve el día siguiente: el usuario elegiría "hoy" en el
//    calendario y pediría mañana.
// ─────────────────────────────────────────────────────────────────────────────

const GRANULARIDADES = [
    { key: 'day', label: 'Día' },
    { key: 'week', label: 'Semana' },
    { key: 'month', label: 'Mes' },
];

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']; // WEEKDAY() de MySQL: 0 = lunes

const money = (value) => (value == null ? '—' : `$${Number(value).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (value) => (value == null ? '—' : Number(value).toLocaleString('de-DE'));
const percent = (value) => (value == null ? '—' : `${Number(value).toFixed(1)}%`);

// VENDIDO e INGRESO (decisión del dueño, 2026-09-30). "Vendido" es lo que pagaron los clientes, y
// va con las ventas y el ticket promedio (vendido / ventas, cierra). El "Ingreso" le resta el
// crédito de los usados vendidos: el usado se vende como en consignación y esa parte del precio
// es plata del vendedor, que vuelve a entrar cuando la gasta en mercadería. No se dice
// "Facturado": se confunde con la facturación electrónica. Los % de margen van sobre el ingreso.
//
// Las GIFT CARDS se comportan igual (decisión del dueño, 2026-10-08): una gift card vendida es
// plata cobrada que todavía se debe en mercadería, así que cuenta en lo vendido y se resta del
// ingreso. Las que vencieron sin usarse ya no se deben y vuelven a sumar. El backend ya manda
// `netRevenue` con todo eso hecho: acá sólo se muestra, no se recalcula.
//
// Contra un backend que todavía no manda `netRevenue`, el ingreso no se muestra y "Vendido" es
// el mismo número de siempre.
const VENDIDO = 'Vendido';
const ingresoDe = (x) => (x && x.netRevenue != null ? x.netRevenue : x ? x.revenue : null);

// Qué se le restó (y sumó) a lo vendido para llegar al ingreso, en palabras. `null` si no hubo
// nada: sin usados ni gift cards el ingreso es lo vendido. Un backend anterior no manda
// `giftCards` y entonces sólo se nombran los usados, como siempre.
function explicacionDelIngreso(summary) {
    if (!summary) return null;
    const usados = summary.usedGames && summary.usedGames.credit > 0 ? summary.usedGames : null;
    const gc = summary.giftCards || {};
    const partes = [];
    if (usados) partes.push(`− ${money(usados.credit)} de crédito de ${number(usados.units)} usado(s), que es de los vendedores`);
    if (gc.sold > 0) partes.push(`− ${money(gc.sold)} de gift cards vendidas, que todavía se deben en mercadería`);
    if (gc.expired > 0) partes.push(`+ ${money(gc.expired)} de gift cards que vencieron sin usarse`);
    // Premios y cortesías usados o canjeados: lo regalado es descuento, no ingreso.
    if (gc.given > 0) partes.push(`− ${money(gc.given)} de premios y cortesías entregados`);
    return partes.length > 0 ? `vendido ${partes.join(' ')}` : null;
}

// Fecha local -> 'YYYY-MM-DD' sin pasar por UTC (ver regla 3).
function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function variacion(actual, anterior) {
    if (actual == null || anterior == null || anterior === 0) return null;
    return ((actual - anterior) / anterior) * 100;
}

class SalesAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        desde.setMonth(desde.getMonth() - 11);
        desde.setDate(1);

        this.state = {
            // Años superpuestos en la comparativa. null = todavía no se eligió y se toman los
            // dos más recientes; un Set vacío es una elección válida (no mostrar ninguno).
            aniosElegidos: null,
            from: desde,
            to: hoy,
            granularity: 'month',
            channel: '',
            originator: '',
            // La tabla existía antes de esta pantalla y se perdió al pasar a gráfico. Para comparar
            // dos meses puntuales, leer números es más rápido que estimar alturas.
            vistaEvolucion: 'grafico',
            loaded: false,
            error: null,
            data: null,
        };
    }

    componentDidMount() {
        this.cargar();
    }

    cargar = () => {
        this.setState({ loaded: false, error: null });

        const branchId = getSelectedBranchId();
        const params = new URLSearchParams({
            from: toIsoDate(this.state.from),
            to: toIsoDate(this.state.to),
            granularity: this.state.granularity,
        });
        if (branchId) params.set('branchId', branchId);
        if (this.state.channel) params.set('channel', this.state.channel);
        if (this.state.originator) params.set('originator', this.state.originator);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/sales?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                        return;
                    }
                    // Un rango inválido (6400) llega con el detalle: se muestra en la pantalla y
                    // no en un alert, porque es un error de lo que el usuario acaba de elegir.
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    exportarCsv = () => {
        const { data } = this.state;
        if (!data) return;

        // Las columnas de gift cards sólo van si hubo alguna en el rango (mismo criterio que la
        // tabla): contra un backend anterior, o sin gift cards, el archivo es el de siempre.
        const conGiftCards = data.series.some((s) => (s.giftCardsSold || 0) > 0);
        const conVencidas = data.series.some((s) => (s.giftCardsExpired || 0) > 0);
        const conPremios = data.series.some((s) => (s.giftCardsGiven || 0) > 0);
        const filas = [['Período', 'Ventas', VENDIDO, 'Unidades', 'Ticket promedio', 'Crédito de usados',
            ...(conGiftCards ? ['Gift cards vendidas'] : []),
            ...(conVencidas ? ['Gift cards vencidas'] : []),
            ...(conPremios ? ['Premios y cortesías'] : []),
            'Ingreso', 'Margen']];
        data.series.forEach((s) => {
            // Redondeado: el separador `;` + BOM apuntan a un Excel con coma decimal, donde
            // "212088.3250000003" no parsea como número y queda texto. Los exports de Clientes e
            // Inventario ya redondeaban; éste no.
            filas.push([s.bucket, s.orders, Math.round(s.revenue), s.units,
                s.avgTicket == null ? '' : Math.round(s.avgTicket),
                // Vacío y no 0 contra un backend que no los manda: 0 diría "no hubo".
                s.usedGamesCredit == null ? '' : Math.round(s.usedGamesCredit),
                ...(conGiftCards ? [Math.round(s.giftCardsSold || 0)] : []),
                ...(conVencidas ? [Math.round(s.giftCardsExpired || 0)] : []),
                ...(conPremios ? [Math.round(s.giftCardsGiven || 0)] : []),
                s.netRevenue == null ? '' : Math.round(s.netRevenue),
                s.margin == null ? '' : Math.round(s.margin)]);
        });

        // BOM para que Excel abra los acentos bien (mismo criterio que el export de ML).
        const csv = '﻿' + filas.map((f) => f.join(';')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        // El nombre sale del rango de LOS DATOS, no de los pickers: mover los pickers sin apretar
        // Aplicar y exportar generaba un archivo cuyo nombre mentía el período para siempre.
        a.download = `ventas-${data.range.from}-a-${data.range.to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── POR QUÉ TODAS LAS COMPARATIVAS DE ESTA PANTALLA SON INTERANUALES ──────────────────────
    //
    // El negocio es fuertemente estacional: diciembre vale casi cuatro meses típicos y enero casi
    // dos. Comparar contra "el período inmediatamente anterior del mismo largo" mide entonces en
    // qué parte del año cae la ventana, no cómo va el negocio.
    //
    // El caso que lo destapó: el rango 2026-01-01 → 2026-09-04 (247 días) tiene como período
    // anterior 2025-04-29 → 2025-12-31, que se lleva adentro toda la Navidad. La tarjeta de
    // Ingreso daba "+4.8%" mientras que contra enero-setiembre de 2025 el mismo ingreso era
    // +25.7%. Los dos números estaban bien: la tarjeta decía "vs. período anterior" sin aclarar
    // cuál, y se leía como el segundo.
    //
    // Por eso la base es siempre `comparison.yearAgo` y la tarjeta ESCRIBE el año contra el que
    // compara. `comparison.previous` lo sigue devolviendo el backend, pero no se muestra en
    // ningún lado: se puede sacar del payload en una release posterior (paso de contract).
    renderKpi(titulo, valor, anterior, formato = money, extra = null) {
        const v = variacion(valor, anterior);
        const signo = v == null ? '' : v >= 0 ? '+' : '';
        const color = v == null ? 'text-muted' : v >= 0 ? 'text-success' : 'text-danger';
        const anioBase = String(this.state.data.comparison.yearAgo.from).slice(0, 4);

        return (
            <div className="col-xl col-lg-4 col-md-6 col-sm-12 mb-3" key={titulo}>
                <div className="stats-small stats-small--1 card card-small h-100">
                    <div className="card-body p-3">
                        <span className="stats-small__label text-uppercase text-muted d-block">{titulo}</span>
                        <h6 className="stats-small__value count my-2">{formato(valor)}</h6>
                        <span className={`stats-small__percentage ${color}`}>
                            {v == null ? 'sin comparativa' : `${signo}${v.toFixed(1)}% vs. mismo período de ${anioBase}`}
                        </span>
                        {extra}
                    </div>
                </div>
            </div>
        );
    }

    // ── Comparativa mes a mes entre años (ENI-260) ────────────────────────────────────────────
    //
    // Es otra pregunta que la serie del rango: no "cómo viene el período" sino "cómo viene agosto
    // contra los agostos anteriores". Los años se prenden y apagan porque con cinco años de
    // historia superponerlos todos no se lee, y fijar "contra el año pasado" esconde si la
    // tendencia es sostenida o fue un pico.
    aniosVisibles() {
        const { data, aniosElegidos } = this.state;
        const disponibles = (data.yearOverYear || []).map((a) => a.year);
        // Default: los dos más recientes. Superponer cinco años de arranque es ilegible.
        if (aniosElegidos == null) return new Set(disponibles.slice(0, 2));
        return aniosElegidos;
    }

    alternarAnio = (anio) => {
        const actuales = new Set(this.aniosVisibles());
        if (actuales.has(anio)) actuales.delete(anio); else actuales.add(anio);
        this.setState({ aniosElegidos: actuales });
    };

    // ── La cascada de costos (ENI-262) ────────────────────────────────────────────────────────
    //
    // El panel mostraba "margen bruto" y "margen neto" y la diferencia entre los dos era sólo la
    // comisión de ML — que además falta en el 96% de las líneas, así que los dos números eran
    // prácticamente el mismo con distinto nombre. Acá cada deducción es una fila y el total no
    // cierra si se saltea alguna.
    // ── La evolución como TABLA ───────────────────────────────────────────────────────────────
    //
    // Un gráfico responde "cómo viene la tendencia"; una tabla responde "cuánto fue marzo contra
    // abril". La segunda pregunta se contesta mucho mejor leyendo números que estimando alturas,
    // y es la que se hacía con la tabla vieja de Estadísticas generales.
    //
    // Lleva MÁS columnas que series tiene el gráfico: unidades, ticket promedio y margen % no
    // entran en un eje sin volverlo ilegible, pero en una tabla no cuestan nada.
    renderTablaEvolucion() {
        const { series } = this.state.data;
        if (!series || series.length === 0) {
            return <div className="card-body"><p className="text-muted mb-0">Sin ventas en el período.</p></div>;
        }

        // Los totales van al pie y se calculan sobre lo mostrado: si se suman aparte de la tabla,
        // un día la tabla filtra algo y el total sigue diciendo otra cosa.
        //
        // Suma que RESPETA el null. Antes hacía `a.margin + (f.margin || 0)`: con canal Eventos
        // —donde ningún bucket tiene costo— la columna de cada período mostraba "—" y el Total de
        // esa misma columna decía "$0 / 0,0%". Se leía "eventos no deja nada". Y en un rango
        // mixto el total subdeclaraba en silencio, sin marca de que faltaban períodos.
        const sumarONull = (acumulado, valor) => (valor == null || acumulado == null ? null : acumulado + valor);
        // Las columnas del crédito de usados y del ingreso sólo aparecen si hubo alguno en el rango:
        // sin usados, el ingreso es igual a lo vendido y la columna repetiría el mismo número.
        const conUsados = series.some((f) => (f.usedGamesCredit || 0) > 0);
        // Lo mismo con las gift cards vendidas y con las vencidas (que suman). El ingreso aparece
        // si hubo CUALQUIERA de las tres: con gift cards y sin usados también difiere de lo vendido.
        const conGiftCards = series.some((f) => (f.giftCardsSold || 0) > 0);
        const conVencidas = series.some((f) => (f.giftCardsExpired || 0) > 0);
        // Premios y cortesías entregados (usados o canjeados): restan, como descuento.
        const conPremios = series.some((f) => (f.giftCardsGiven || 0) > 0);
        const conIngreso = conUsados || conGiftCards || conVencidas || conPremios;
        const total = series.reduce((a, f) => ({
            usedGamesCredit: a.usedGamesCredit + (f.usedGamesCredit || 0),
            giftCardsSold: a.giftCardsSold + (f.giftCardsSold || 0),
            giftCardsExpired: a.giftCardsExpired + (f.giftCardsExpired || 0),
            giftCardsGiven: a.giftCardsGiven + (f.giftCardsGiven || 0),
            netRevenue: a.netRevenue + (ingresoDe(f) || 0),
            orders: a.orders + (f.orders || 0),
            units: a.units + (f.units || 0),
            revenue: a.revenue + (f.revenue || 0),
            margin: sumarONull(a.margin, f.margin),
            vat: sumarONull(a.vat, f.vat),
            shipping: sumarONull(a.shipping, f.shipping),
            contributionMargin: sumarONull(a.contributionMargin, f.contributionMargin),
        }), { usedGamesCredit: 0, giftCardsSold: 0, giftCardsExpired: 0, giftCardsGiven: 0, netRevenue: 0, orders: 0, units: 0, revenue: 0, margin: 0, vat: 0, shipping: 0, contributionMargin: 0 });

        return (
            <div className="card-body p-0">
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0">Período</th>
                                <th className="border-0 text-right">Ventas</th>
                                <th className="border-0 text-right">Unidades</th>
                                <th className="border-0 text-right">{VENDIDO}</th>
                                <th className="border-0 text-right">Ticket promedio</th>
                                {conUsados && <th className="border-0 text-right">Crédito de usados</th>}
                                {conGiftCards && <th className="border-0 text-right">Gift cards vendidas</th>}
                                {conVencidas && <th className="border-0 text-right">Gift cards vencidas</th>}
                                {conPremios && <th className="border-0 text-right">Premios y cortesías</th>}
                                {conIngreso && <th className="border-0 text-right">Ingreso</th>}
                                <th className="border-0 text-right">Margen bruto</th>
                                <th className="border-0 text-right">%</th>
                                {/* Las deducciones que van del bruto al real, por período. La
                                    diferencia entre las dos últimas columnas es lo que la pantalla
                                    llamaba "margen neto" y no descontaba. */}
                                <th className="border-0 text-right">IVA</th>
                                <th className="border-0 text-right">Envío</th>
                                <th className="border-0 text-right">Margen real</th>
                                <th className="border-0 text-right">%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {series.map((f) => (
                                <tr key={f.bucket}>
                                    <td>{f.bucket}</td>
                                    <td className="text-right">{number(f.orders)}</td>
                                    <td className="text-right">{number(f.units)}</td>
                                    <td className="text-right">{money(f.revenue)}</td>
                                    <td className="text-right">{money(f.avgTicket)}</td>
                                    {conUsados && (
                                        <td className={`text-right ${f.usedGamesCredit > 0 ? 'text-danger' : 'text-muted'}`}>
                                            {money(f.usedGamesCredit > 0 ? -f.usedGamesCredit : 0)}
                                        </td>
                                    )}
                                    {conGiftCards && (
                                        <td className={`text-right ${f.giftCardsSold > 0 ? 'text-danger' : 'text-muted'}`}>
                                            {money(f.giftCardsSold > 0 ? -f.giftCardsSold : 0)}
                                        </td>
                                    )}
                                    {conVencidas && (
                                        <td className={`text-right ${f.giftCardsExpired > 0 ? '' : 'text-muted'}`}>
                                            {money(f.giftCardsExpired || 0)}
                                        </td>
                                    )}
                                    {conPremios && (
                                        <td className={`text-right ${f.giftCardsGiven > 0 ? 'text-danger' : 'text-muted'}`}>
                                            {money(f.giftCardsGiven > 0 ? -f.giftCardsGiven : 0)}
                                        </td>
                                    )}
                                    {conIngreso && <td className="text-right">{money(ingresoDe(f))}</td>}
                                    <td className="text-right">{money(f.margin)}</td>
                                    <td className="text-right">
                                        {/* El % se recalcula por fila y no se hereda del total: un mes
                                            flojo con margen alto es exactamente lo que se viene a ver.
                                            Sobre el INGRESO: el crédito de los usados no es del local,
                                            y la gift card vendida todavía se debe en mercadería. */}
                                        {ingresoDe(f) > 0 && f.margin != null ? percent((f.margin / ingresoDe(f)) * 100) : '—'}
                                    </td>
                                    <td className="text-right text-muted">{money(f.vat === 0 ? 0 : -f.vat)}</td>
                                    <td className={`text-right ${f.shipping > 0 ? 'text-danger' : 'text-muted'}`}>
                                        {money(f.shipping === 0 ? 0 : -f.shipping)}
                                    </td>
                                    <td className="text-right font-weight-bold">{money(f.contributionMargin)}</td>
                                    <td className="text-right">{percent(f.contributionMarginPct)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-light font-weight-bold">
                                <td>Total</td>
                                <td className="text-right">{number(total.orders)}</td>
                                <td className="text-right">{number(total.units)}</td>
                                <td className="text-right">{money(total.revenue)}</td>
                                <td className="text-right">{money(total.orders > 0 ? total.revenue / total.orders : null)}</td>
                                {conUsados && <td className="text-right">{money(total.usedGamesCredit > 0 ? -total.usedGamesCredit : 0)}</td>}
                                {conGiftCards && <td className="text-right">{money(total.giftCardsSold > 0 ? -total.giftCardsSold : 0)}</td>}
                                {conVencidas && <td className="text-right">{money(total.giftCardsExpired)}</td>}
                                {conPremios && <td className="text-right">{money(total.giftCardsGiven > 0 ? -total.giftCardsGiven : 0)}</td>}
                                {conIngreso && <td className="text-right">{money(total.netRevenue)}</td>}
                                <td className="text-right">{money(total.margin)}</td>
                                <td className="text-right">
                                    {total.netRevenue > 0 && total.margin != null ? percent((total.margin / total.netRevenue) * 100) : '—'}
                                </td>
                                <td className="text-right">{money(total.vat === 0 ? 0 : -total.vat)}</td>
                                <td className="text-right">{money(total.shipping === 0 ? 0 : -total.shipping)}</td>
                                <td className="text-right">{money(total.contributionMargin)}</td>
                                <td className="text-right">
                                    {total.netRevenue > 0 && total.contributionMargin != null ? percent((total.contributionMargin / total.netRevenue) * 100) : '—'}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    }

    renderCascada() {
        const { data } = this.state;
        const w = data.summary.waterfall;
        const cob = data.summary.deductionCoverage;
        if (!w) return null;

        // El paso de lo vendido al ingreso: la parte del precio de los usados que es del vendedor
        // y las gift cards vendidas, que todavía se deben en mercadería; las que vencieron sin
        // usarse ya no se deben y vuelven a sumar. Cada fila sólo si hubo (un backend anterior no
        // las manda), y el INGRESO aparece con CUALQUIERA de ellas: colgarlo sólo de los usados lo
        // escondía en un período con gift cards y sin usados.
        const alIngreso = [
            ...(w.usedGamesCredit > 0 ? [{ t: 'Crédito de usados (plata de los vendedores)', v: -w.usedGamesCredit }] : []),
            ...(w.giftCardsSold > 0 ? [{ t: 'Gift cards vendidas (plata por canjear)', v: -w.giftCardsSold }] : []),
            ...(w.giftCardsExpired > 0 ? [{ t: 'Gift cards vencidas sin usar', v: w.giftCardsExpired }] : []),
            ...(w.giftCardsGiven > 0 ? [{ t: 'Premios y cortesías (regalado)', v: -w.giftCardsGiven }] : []),
        ];
        const filas = [
            { t: VENDIDO, v: w.revenue, tipo: 'base' },
            ...alIngreso,
            ...(alIngreso.length > 0 ? [{ t: 'INGRESO', v: w.netRevenue, tipo: 'subtotal' }] : []),
            { t: 'Costo de mercadería', v: -w.goodsCost },
            { t: 'MARGEN BRUTO', v: w.grossMargin, pct: w.grossMarginPct, tipo: 'subtotal' },
            { t: 'Comisión de MercadoLibre', v: -w.deductions.mlFee },
            { t: 'Comisión de tarjeta', v: -w.deductions.cardFee, flojo: !cob.cardRatesConfigured },
            { t: 'IVA (neto de compras)', v: -w.deductions.vat },
            { t: 'Resultado del envío', v: -w.deductions.shipping },
            { t: 'MARGEN DE CONTRIBUCIÓN', v: w.contributionMargin, pct: w.contributionMarginPct, tipo: 'total' },
        ];

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">De la venta al bolsillo</h6>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                        Qué queda de cada peso vendido después de TODOS los costos. El margen bruto sólo
                        descuenta la mercadería.
                    </span>
                </div>
                <div className="card-body p-0">
                    <table className="table table-sm mb-0">
                        <tbody>
                            {filas.map((f) => (
                                <tr key={f.t} className={f.tipo === 'total' ? 'bg-light' : ''}>
                                    <td
                                        className={f.tipo === 'subtotal' || f.tipo === 'total' ? 'font-weight-bold' : ''}
                                        style={f.tipo ? undefined : { paddingLeft: 28 }}
                                    >
                                        {f.t}
                                        {f.flojo && (
                                            <span className="badge badge-warning ml-2" style={{ fontSize: 9 }}>sin tasa cargada</span>
                                        )}
                                    </td>
                                    <td className={`text-right ${f.tipo === 'subtotal' || f.tipo === 'total' ? 'font-weight-bold' : ''} ${f.v < 0 ? 'text-danger' : ''}`}>
                                        {/* `+0` normaliza el -0 de JS: negar cero da -0 y se imprime "$-0". */}
                                        {money(f.v === 0 ? 0 : f.v)}
                                    </td>
                                    <td className="text-right text-muted" style={{ width: 80, fontSize: 12 }}>
                                        {f.pct != null ? percent(f.pct) : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="card-body pt-2" style={{ fontSize: 12 }}>
                    {/* Sin esto el número final se lee como definitivo, y hoy es un techo. */}
                    <strong className="d-block mb-1">Por qué esto todavía es un techo</strong>
                    {!cob.cardRatesConfigured && (
                        <div className="text-danger">
                            • La comisión de tarjeta <strong>no se está descontando</strong>: no hay ninguna
                            tasa cargada, y son {money(cob.cardRevenue)} facturados con POS y Mercado Pago.
                        </div>
                    )}
                    {/* La comisión de MP no sale de una tasa: sale del cobro vinculado. Donde no
                        hay cobro, NO se descuenta nada —estimarla con un promedio sería el número
                        preciso-pero-falso que este panel existe para no mostrar—. Sin esta línea la
                        cascada se lee como completa justo cuando más corta está. */}
                    {cob.mpSales > 0 && cob.mpSalesWithPayment < cob.mpSales && (
                        <div className={cob.mpSalesWithPayment === 0 ? 'text-danger' : ''}>
                            • La comisión de <strong>Mercado Pago</strong> está medida en{' '}
                            {number(cob.mpSalesWithPayment)} de {number(cob.mpSales)} ventas
                            ({percent(cob.mpFeeCoverage * 100)}). En las otras{' '}
                            {number(cob.mpSales - cob.mpSalesWithPayment)} <strong>no se descuenta
                            nada</strong>: no hay cobro vinculado y no se estima con un promedio.
                            Las anteriores a que la API de Mercado Pago entregue datos no van a
                            poder medirse nunca.
                        </div>
                    )}
                    {cob.shipmentsWithoutCost > 0 && (
                        <div>
                            • {number(cob.shipmentsWithoutCost)} de {number(cob.shipments)} envíos no tienen
                            costo cargado: lo que costaron figura como cero y el resultado del envío sale
                            mejor de lo que es.
                        </div>
                    )}
                    {/* No es un "techo": es cómo se lee la línea de envío. Sin esta aclaración,
                        la deducción parece más grande de lo que "se cobró menos lo que costó". */}
                    {cob.shippingChargedInRevenue > 0 && (
                        <div>
                            • {money(cob.shippingChargedInRevenue)} del envío cobrado ya están dentro del
                            ingreso —la tienda web y el mostrador lo facturan en el mismo ticket— así que
                            no se acreditan de nuevo contra su costo. Por MercadoLibre se cobra aparte y
                            sí se acredita.
                        </div>
                    )}
                    {cob.shippingUnclassified > 0 && (
                        <div>
                            • {number(cob.shippingUnclassified)} envíos no se pudieron clasificar (un cupón
                            movió el total del ticket). No se acreditan: ante la duda, el lado prudente.
                        </div>
                    )}
                    <div>
                        • El IVA va <strong>neto</strong>: 18,03% de la venta menos 18,03% del costo, que es
                        lo que efectivamente se le paga a DGI.
                    </div>
                </div>
            </div>
        );
    }

    renderComparativaAnios() {
        const { data } = this.state;
        const anios = data.yearOverYear || [];
        if (anios.length === 0) return null;

        const visibles = this.aniosVisibles();
        const activos = anios.filter((a) => visibles.has(a.year));
        const incompletos = activos.filter((a) => a.monthsWithData < 12);

        // Una fila por mes; una columna por año elegido.
        const filas = MESES.map((nombre, i) => {
            const mes = i + 1;
            const fila = { mes: nombre };
            activos.forEach((a) => { fila[String(a.year)] = a.months[mes] ? a.months[mes].revenue : null; });
            return fila;
        });

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-start flex-wrap">
                    <div>
                        <h6 className="m-0">Comparativa por año</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Lo vendido mes a mes. Prendé y apagá años para compararlos.
                        </span>
                    </div>
                    <div className="btn-group btn-group-sm mt-1">
                        {anios.map((a, i) => (
                            <button
                                key={a.year}
                                className={`btn ${visibles.has(a.year) ? 'btn-primary' : 'btn-white'}`}
                                onClick={() => this.alternarAnio(a.year)}
                                style={visibles.has(a.year)
                                    ? { backgroundColor: COLORES_ANIO[i % COLORES_ANIO.length], borderColor: COLORES_ANIO[i % COLORES_ANIO.length] }
                                    : undefined}
                            >{a.year}</button>
                        ))}
                    </div>
                </div>
                <div className="card-body">
                    {incompletos.length > 0 && (
                        // Sin este aviso, el año en curso siempre parece peor: le faltan meses.
                        <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                            {incompletos.map((a) => `${a.year} tiene ${a.monthsWithData} de 12 meses`).join(' · ')}
                            {' '}— los meses que faltan no se dibujan, no valen cero.
                        </p>
                    )}
                    {activos.length === 0
                        ? <p className="text-muted mb-0">Elegí al menos un año.</p>
                        : (
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={filas}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={ejeMoneda} width={70} />
                                    <Tooltip formatter={(v) => money(v)} />
                                    <Legend />
                                    {activos.map((a) => {
                                        const i = anios.findIndex((x) => x.year === a.year);
                                        return (
                                            <Bar
                                                key={a.year}
                                                dataKey={String(a.year)}
                                                name={String(a.year)}
                                                fill={COLORES_ANIO[i % COLORES_ANIO.length]}
                                                radius={[3, 3, 0, 0]}
                                                isAnimationActive={false}
                                            />
                                        );
                                    })}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                </div>
            </div>
        );
    }

    renderHeatmap(hourly) {
        // ENI-260: la grilla vive en HeatmapComponent, compartida con Salón. El valor va ADENTRO
        // de la celda: antes sólo estaba en el `title` nativo, que tarda medio segundo y no se
        // descubre — el color decía "acá pasa algo" sin decir cuánto.
        return (
            <HeatmapComponent
                cells={(hourly || []).map((h) => ({
                    weekday: h.weekday,
                    hour: h.hour,
                    value: h.revenue,
                    detail: `${DIAS[h.weekday]} ${h.hour}:00 — ${money(h.revenue)} en ${number(h.orders)} venta(s)`,
                }))}
                emptyText="Sin ventas en el período."
            />
        );
    }

    renderDesglose(titulo, filas, ayuda) {
        return (
            <div className="col-lg-6 col-md-12 mb-4">
                <div className="card card-small h-100">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">{titulo}</h6>
                        {ayuda && <span className="text-muted" style={{ fontSize: 12 }}>{ayuda}</span>}
                    </div>
                    <div className="card-body p-0">
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">&nbsp;</th>
                                    <th className="border-0 text-right">Ventas</th>
                                    <th className="border-0 text-right">{VENDIDO}</th>
                                    <th className="border-0 text-right">Margen</th>
                                    <th className="border-0 text-right">Margen neto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filas.length === 0 && (
                                    <tr><td colSpan="5" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>
                                )}
                                {filas.map((f) => (
                                    <tr key={f.key}>
                                        <td>{f.key}</td>
                                        <td className="text-right">{number(f.orders)}</td>
                                        <td className="text-right">{money(f.revenue)}</td>
                                        <td className="text-right">{money(f.margin)}</td>
                                        {/* El neto sólo difiere del bruto donde hay comisión (ML). */}
                                        <td className="text-right">{money(f.netMargin)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loaded, error, data } = this.state;

        return (
            <div>
                {/* ── Filtros ── */}
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Desde</label>
                                <DatePicker
                                    selected={this.state.from}
                                    onChange={(d) => this.setState({ from: d })}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker
                                    selected={this.state.to}
                                    onChange={(d) => this.setState({ to: d })}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Agrupar por</label>
                                <div className="btn-group">
                                    {GRANULARIDADES.map((g) => (
                                        <button
                                            key={g.key}
                                            type="button"
                                            className={`btn btn-sm ${this.state.granularity === g.key ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => this.setState({ granularity: g.key })}
                                        >{g.label}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Canal</label>
                                <select
                                    className="form-control"
                                    value={this.state.channel}
                                    onChange={(e) => this.setState({ channel: e.target.value })}
                                >
                                    <option value="">Todos</option>
                                    <option value="retail">Retail</option>
                                    <option value="cafeteria">Cafetería</option>
                                    <option value="mesas">Mesas</option>
                                    <option value="eventos">Eventos</option>
                                    <option value="membresias">Membresías</option>
                                </select>
                            </div>
                            <div className="mr-3 mb-2">
                                {/* "Canal" es la unidad de negocio (retail/cafetería/mesas) y "origen"
                                    es por dónde entró la venta. Son ejes distintos y se combinan:
                                    "retail + Mercado Libre" es una pregunta legítima. */}
                                <label className="d-block" style={{ fontSize: 12 }}>Origen</label>
                                <select
                                    className="form-control"
                                    value={this.state.originator}
                                    onChange={(e) => this.setState({ originator: e.target.value })}
                                >
                                    <option value="">Todos</option>
                                    <option value="Local">Local (mostrador)</option>
                                    <option value="Mercado Libre">Mercado Libre</option>
                                    <option value="Página">Página (tienda online)</option>
                                </select>
                            </div>
                            <div className="mb-2">
                                <button className="btn btn-primary mr-2" onClick={this.cargar}>Aplicar</button>
                                <button className="btn btn-white" onClick={this.exportarCsv} disabled={!data}>Exportar CSV</button>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-warning">{error}</div>}
                {!loaded && <div className="text-center text-muted py-5">Cargando…</div>}

                {loaded && data && (
                    <div>
                        {/* ── KPIs ── */}
                        <div className="row">
                            {this.renderKpi(VENDIDO, data.summary.revenue, data.comparison.yearAgo.summary.revenue, money,
                                <span className="d-block text-muted" style={{ fontSize: 11 }}>lo que pagaron los clientes</span>)}
                            {this.renderKpi('Ventas', data.summary.orders, data.comparison.yearAgo.summary.orders, number)}
                            {this.renderKpi('Ticket promedio', data.summary.avgTicket, data.comparison.yearAgo.summary.avgTicket)}
                            {data.summary.netRevenue != null && this.renderKpi(
                                'Ingreso',
                                data.summary.netRevenue,
                                ingresoDe(data.comparison.yearAgo.summary),
                                money,
                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                    {explicacionDelIngreso(data.summary) || 'lo que queda para el local'}
                                </span>
                            )}
                            {this.renderKpi(
                                'Margen bruto',
                                data.summary.margin,
                                data.comparison.yearAgo.summary.margin,
                                money,
                                // La cobertura va PEGADA al margen. Un margen calculado sobre una
                                // porción del ingreso no es el margen del negocio, y el usuario
                                // tiene que verlo sin ir a buscarlo.
                                <>
                                    {data.summary.marginPct != null && (
                                        <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                            {percent(data.summary.marginPct)} del ingreso
                                        </span>
                                    )}
                                    {data.summary.costCoverage != null && data.summary.costCoverage < 99.5 && (
                                        <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                            calculado sobre el {percent(data.summary.costCoverage)} de lo vendido
                                            (el resto no tiene costo cargado)
                                        </span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ── Base de comparación ──
                            Las tarjetas ya traen cada una su variación interanual; esta franja dice
                            contra QUÉ. El porcentaje que estaba acá se fue: era el de la tarjeta de
                            Ingreso repetido y, al lado de un "+4.8%" que comparaba contra otra cosa,
                            los dos números parecían estar en disputa. */}
                        <div className="card card-small mb-4">
                            <div className="card-body py-2 d-flex flex-wrap" style={{ fontSize: 13 }}>
                                <span className="mr-4">
                                    <strong>Comparado contra el mismo período de {String(data.comparison.yearAgo.from).slice(0, 4)}</strong>
                                    {' '}({data.comparison.yearAgo.from} a {data.comparison.yearAgo.to}):
                                </span>
                                {data.comparison.yearAgo.summary.orders > 0 ? (
                                    <>
                                        <span className="mr-4">{money(data.comparison.yearAgo.summary.revenue)} vendidos</span>
                                        <span className="mr-4">{number(data.comparison.yearAgo.summary.orders)} ventas</span>
                                        <span>{money(data.comparison.yearAgo.summary.avgTicket)} de ticket</span>
                                    </>
                                ) : (
                                    // Cafetería, mesas y eventos arrancaron en 2026: con esos filtros no hay
                                    // año anterior contra el cual medir. Decirlo es mejor que mostrar "$0 de
                                    // ingreso", que se lee como una caída del 100%.
                                    <span className="text-muted">
                                        no hubo ventas en ese período con estos filtros, así que las tarjetas quedan sin comparativa
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* ── Serie ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom d-flex justify-content-between align-items-center flex-wrap">
                                <h6 className="m-0">Evolución</h6>
                                <div className="btn-group btn-group-sm">
                                    {[['grafico', 'Gráfico'], ['tabla', 'Tabla']].map(([k, t]) => (
                                        <button
                                            key={k}
                                            className={`btn ${this.state.vistaEvolucion === k ? 'btn-primary' : 'btn-white'}`}
                                            onClick={() => this.setState({ vistaEvolucion: k })}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>
                            {this.state.vistaEvolucion === 'tabla' ? this.renderTablaEvolucion() : (
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <ComposedChart data={data.series}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="bucket" />
                                        {/* `width` explícito además del formateador: con etiquetas de
                                            4-5 caracteres el default alcanza, pero si el rango cambia
                                            vuelve a recortar en silencio. */}
                                        <YAxis yAxisId="izq" tickFormatter={ejeMoneda} width={70} tick={{ fontSize: 11 }} />
                                        <YAxis yAxisId="der" orientation="right" width={45} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(value, name) => (name === 'Ventas' ? number(value) : money(value))} />
                                        <Legend />
                                        {/* isAnimationActive={false}: si el componente re-renderiza mientras corre el
                                            tween de entrada, recharts deja las series congeladas — acá quedaban en
                                            altura CERO, con ejes y leyenda dibujados, así que el gráfico parecía
                                            "sin datos" en vez de roto. */}
                                        <Bar yAxisId="der" dataKey="orders" name="Ventas" fill="#c4d8f0" isAnimationActive={false} />
                                        <Line yAxisId="izq" dataKey="revenue" name={VENDIDO} stroke="#007bff" strokeWidth={2} dot={false} isAnimationActive={false} />
                                        <Line yAxisId="izq" dataKey="margin" name="Margen" stroke="#17c671" strokeWidth={2} dot={false} isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                            )}
                        </div>

                        {/* ── Desgloses ── */}
                        <div className="row">
                            {this.renderDesglose('Por canal', data.byChannel, 'La unidad de negocio de la venta.')}
                            {this.renderDesglose('Por origen', data.byOriginator, 'De dónde entró la venta: mostrador, web, MercadoLibre o cada feria.')}
                        </div>

                        {/* ── Heatmap ── */}
                        {this.renderCascada()}

                        {this.renderComparativaAnios()}

                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Cuándo se vende</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>Lo vendido por día de la semana y hora.</span>
                            </div>
                            <div className="card-body">{this.renderHeatmap(data.hourly)}</div>
                        </div>

                        {/* ── Anulaciones y descuentos ── */}
                        <div className="row">
                            <div className="col-lg-6 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom"><h6 className="m-0">Anulaciones</h6></div>
                                    <div className="card-body">
                                        <p className="mb-1">{number(data.cancellations.orders)} venta(s) anulada(s)</p>
                                        <p className="text-muted mb-0">{money(data.cancellations.revenue)} que no se facturaron</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom"><h6 className="m-0">Descuentos</h6></div>
                                    <div className="card-body">
                                        <p className="mb-1">{money(data.discounts.discountAmount)} de diferencia contra el precio de catálogo</p>
                                        {data.discounts.coupons.length === 0
                                            ? <p className="text-muted mb-0">Sin cupones usados en el período.</p>
                                            : (
                                                <ul className="mb-0 pl-3">
                                                    {data.discounts.coupons.map((c) => (
                                                        <li key={c.code}>{c.code}: {number(c.orders)} venta(s), {money(c.revenue)}</li>
                                                    ))}
                                                </ul>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default SalesAnalyticsComponent;
